import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Divider, Grid, IconButton, Typography } from '@mui/material';
import moment from 'moment';

import { areUserRolesIn, formatEntityId, getDurationFromSeconds } from '../../../../helpers';
import {
  FORTA_GRAY4,
  FORTA_BLUE,
  FORTA_GRAY3,
  FORTA_GRAY2,
  FORTA_WHITE,
} from '../../../../utils/colors';
import { getSupervisionStatus } from '../../../../utils/common';
import { history } from '../../../../helpers';
import { FHButton, FHConfirmDialog, FHTooltip } from '../../../base';
import { clientAssessmentActions } from '../../../../store';
import { AssessmentNoteStatus, Roles } from '../../../../constants';
import DownloadButton from '../../../svgs/DownloadButton';
import BcbaIcon from '../../../../assets/images/bcba.svg';
import BTIcon from '../../../../assets/images/bt.svg';
import ClientIcon from '../../../../assets/images/client-card.svg';
import TrashIcon from '../../../svgs/TrashIcon3';
import { useEditAssessment, useBackdateAssessment } from './hooks';
import { AssessmentBackdateForm } from './components';

import type { RootState } from '../../../../store';
import type { IClientAssessment } from '../../../../utils/types';

interface ISessionNotesQACardProps {
  assessment: IClientAssessment;
}

function AssessmentInfoCard(props: ISessionNotesQACardProps) {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useSelector((state: RootState) => state.auth);

  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState<boolean>(false);
  const {
    id,
    date_start,
    client,
    duration,
    provider,
    status,
    service_code,
    location,
    delivery_method,
  } = props.assessment;
  const assessment_status = getSupervisionStatus(status);
  const showDeleteButton = useMemo(() => {
    return Boolean(
      id && (provider.email === user?.email || areUserRolesIn(user?.roles, [Roles.Admin])),
    );
  }, [id, status, provider, user]);

  const isAdmin = areUserRolesIn(user?.roles, [Roles.Admin]);
  const showReopenButton = Boolean(isAdmin && id && status === AssessmentNoteStatus.Completed);
  const showBackdateForm = Boolean(isAdmin && id && status === AssessmentNoteStatus.Started);

  const { showConfirmEditDialog, setShowConfirmEditDialog, handleEditAssessment } =
    useEditAssessment(id);
  const {
    showConfirmBackdateDialog,
    setShowConfirmBackdateDialog,
    requestBackdate,
    handleBackdateAssessment,
  } = useBackdateAssessment(id);

  const tooltipStyle = {
    '& .MuiTooltip-tooltip': {
      borderRadius: '8px',
      boxShadow: '0px 0px 6px 0px rgba(18, 78, 120, 0.25)',
      color: FORTA_GRAY4,
      marginTop: -5,
      fontWeight: 700,

      '& .MuiTooltip-arrow:before': {
        boxShadow: '0px 0px 6px 0px rgba(18, 78, 120, 0.25)',
      },
    },
  };

  const handleDownload = async () => {
    if (!(props.assessment != null && Object.hasOwn(props.assessment, 'id'))) {
      return;
    }

    const response = await dispatch(
      clientAssessmentActions.downloadAssessmentNote({
        assessmentNoteId: id,
        getAccessTokenSilently,
      }),
    );

    if (response.payload?.document_url) {
      window.location.href = response.payload?.document_url;
      toast.success('Assessment note downloaded');
    }
  };

  const handleDeleteAssessment = async () => {
    if (!id) return;
    setShowConfirmDeleteDialog(false);

    const { error } = await dispatch(
      clientAssessmentActions.deleteAssessmentNote({
        assessmentNoteId: id,
        getAccessTokenSilently,
      }),
    );

    if (error) return;
    history.navigate('/dashboard');
  };

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        backgroundColor: FORTA_WHITE,
        border: `2px solid ${FORTA_BLUE.light3}`,
        boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.25)',
        borderRadius: 2,
        padding: 2,
      }}
    >
      {showBackdateForm && (
        <Box pb={2}>
          <AssessmentBackdateForm assessment={props.assessment} onBackdate={requestBackdate} />
        </Box>
      )}
      <Grid container spacing={1} justifyContent="space-between">
        <Grid
          item
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row',
          }}
        >
          <Grid container spacing={2} justifyContent="space-between">
            <Grid
              item
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box>
                <img src={assessment_status?.icon} alt="icon" width="40" height="40" />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography color={FORTA_GRAY3} align="left">
                    {moment(date_start).format('MM/DD/YYYY')}
                  </Typography>
                  -
                  <Typography color={FORTA_GRAY4} sx={{ fontWeight: 700 }}>
                    {formatEntityId(String(id), 'FA')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 1,
                      width: '140px',
                    }}
                  >
                    <FHTooltip
                      title="CLIENT"
                      arrow
                      sx={tooltipStyle}
                      tooltipbackground={FORTA_WHITE}
                    >
                      <img src={ClientIcon} alt="Client icon" />
                    </FHTooltip>
                    <Typography
                      color={FORTA_GRAY4}
                      align="left"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {client?.first_name} {client?.last_name}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      width: '140px',
                      gap: 1,
                      ml: 3,
                    }}
                  >
                    <FHTooltip
                      title={service_code === '97151' ? 'BCBA' : 'ASSISTANT ASSESSOR'}
                      arrow
                      sx={tooltipStyle}
                      tooltipbackground={FORTA_WHITE}
                    >
                      <img src={service_code === '97151' ? BcbaIcon : BTIcon} alt="BT icon" />
                    </FHTooltip>
                    <Typography
                      color={FORTA_GRAY4}
                      align="left"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {provider?.first_name} {provider?.last_name}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Box
              sx={{
                padding: '16px 0px 0px 8px',
              }}
            >
              <Divider
                sx={{
                  color: FORTA_GRAY2,
                  borderWidth: '1.5px',
                  borderRadius: 2,
                }}
                orientation="vertical"
              />
            </Box>

            <Grid
              item
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                minHeight: '54px',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '8px',
                  minWidth: '100px',
                }}
              >
                <Typography color={FORTA_GRAY3} align="left">
                  Start date, time and duration
                </Typography>

                <Typography color={FORTA_GRAY4} align="left">
                  {date_start ? moment(date_start).format('MM/DD/YYYY - hh:mm A') : 'None'},{' '}
                  {getDurationFromSeconds(duration)}
                </Typography>
              </Box>
            </Grid>
            <Box
              sx={{
                padding: '16px 0px 0px 16px',
              }}
            >
              <Divider
                sx={{
                  color: FORTA_GRAY2,
                  borderWidth: '1.5px',
                  borderRadius: 2,
                }}
                orientation="vertical"
              />
            </Box>
            <Grid
              item
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                minHeight: '54px',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '8px',
                  minWidth: '100px',
                }}
              >
                <Typography color={FORTA_GRAY3} align="left">
                  Client location
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography color={FORTA_GRAY4} align="left">
                    {location || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Box
              sx={{
                padding: '16px 0px 0px 16px',
              }}
            >
              <Divider
                sx={{
                  color: FORTA_GRAY2,
                  borderWidth: '1.5px',
                  borderRadius: 2,
                }}
                orientation="vertical"
              />
            </Box>
            <Grid
              item
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                minHeight: '54px',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '8px',
                  minWidth: '80px',
                }}
              >
                <Typography color={FORTA_GRAY3} align="left">
                  Delivery method
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography color={FORTA_GRAY4} align="left">
                    {delivery_method || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Box
              sx={{
                padding: '16px 0px 0px 16px',
              }}
            >
              <Divider
                sx={{
                  color: FORTA_GRAY2,
                  borderWidth: '1.5px',
                  borderRadius: 2,
                }}
                orientation="vertical"
              />
            </Box>
            <Grid
              item
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                minHeight: '54px',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: '8px',
                  minWidth: '80px',
                }}
              >
                <Typography color={FORTA_GRAY3} align="left">
                  Procedure code
                </Typography>

                <Typography color={FORTA_GRAY4} align="left">
                  {service_code}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            minHeight: '54px',
            gap: 1,
          }}
        >
          {status === AssessmentNoteStatus.Completed && (
            <IconButton sx={{ padding: 0, height: 40 }} onClick={handleDownload}>
              <DownloadButton />
            </IconButton>
          )}
          {showReopenButton && (
            <FHButton onClick={() => setShowConfirmEditDialog(true)}>Edit</FHButton>
          )}
          {showDeleteButton && (
            <IconButton
              sx={{
                p: 1,
                ml: 1,
                opacity: 0.5,
                '&:hover': {
                  opacity: 1,
                },
              }}
              data-testid="deleteClientAssessment"
              onClick={() => setShowConfirmDeleteDialog(true)}
            >
              <TrashIcon color={FORTA_BLUE.primary} width={24} height={24} />
            </IconButton>
          )}
        </Grid>
        <FHConfirmDialog
          open={showConfirmDeleteDialog}
          title={'Delete assessement'}
          content={'Are you sure you want to delete this assessment? This action cannot be undone.'}
          submittext={'Yes, delete'}
          canceltext={'Cancel'}
          submitBtnTestId={'deleteBtn'}
          cancelBtnTestId={'cancelBtn'}
          onClose={() => setShowConfirmDeleteDialog(false)}
          onSubmit={handleDeleteAssessment}
        />
        <FHConfirmDialog
          open={showConfirmEditDialog}
          title="Make editable"
          content="Are you sure you want to make this assessment note editable?"
          submittext="Yes"
          canceltext="No"
          onClose={() => setShowConfirmEditDialog(false)}
          onSubmit={handleEditAssessment}
        />
        <FHConfirmDialog
          open={showConfirmBackdateDialog}
          title="Change"
          content="Are you sure you want to change the start date of this assessment note?"
          submittext="Yes"
          canceltext="No"
          onClose={() => setShowConfirmBackdateDialog(false)}
          onSubmit={handleBackdateAssessment}
        />
      </Grid>
    </Box>
  );
}

export default AssessmentInfoCard;
